import { Controller } from "@hotwired/stimulus"

// The Wellfound jobs embed on /careers.
//
// Their embed_jobs.js finds #angellist_embed, XHRs
// wellfound.com/job_profiles/embed?startup=<slug>, and inserts the response as
// a sibling immediately before the script tag. It gives no callback and fires
// no event, so a MutationObserver on the container is how we learn it arrived.
//
// The block starts hidden and stays hidden unless the embed returns a posting
// we are NOT already rendering server-side. Careers::Role mirrors the live
// Wellfound roles so a crawler can see them; without the dedupe every one of
// those would also appear here, listed twice on the same page.
//
// Hidden is also the correct resting state for every failure mode — Wellfound
// down, the request blocked by an extension, the profile emptied — because the
// server-rendered list above is complete on its own.
export default class extends Controller {
  static targets = ["list"]
  static values = { listed: Array }

  connect() {
    if (!this.hasListTarget) return

    this.observer = new MutationObserver(() => this.#review())
    this.observer.observe(this.listTarget, { childList: true, subtree: true })
    this.#review()

    // The embed is async and silent on failure. Stop watching after a while
    // rather than leaving an observer attached for the life of the page.
    this.timer = setTimeout(() => this.observer?.disconnect(), 15000)
  }

  disconnect() {
    this.observer?.disconnect()
    clearTimeout(this.timer)
  }

  #review() {
    const links = [...this.listTarget.querySelectorAll("a.angellist_jobs-title-link")]
    if (links.length === 0) return

    // Compare on pathname: the same posting can arrive with a different host
    // casing or a tracking query, and a raw string compare would call it new.
    const listed = new Set(this.listedValue.map((url) => this.#key(url)).filter(Boolean))

    // `seen` also catches the same posting twice inside the list. Turbo Drive is
    // on, and a restored snapshot can carry an already-injected job while the
    // embed script runs again over it — the guard makes a double injection
    // harmless instead of showing the role twice.
    const seen = new Set()
    let extra = 0
    links.forEach((link) => {
      const job = link.closest(".angellist_jobs-job") || link
      const key = this.#key(link.href)
      const duplicate = listed.has(key) || seen.has(key)
      seen.add(key)
      job.hidden = duplicate
      if (!duplicate) extra += 1
    })

    this.element.hidden = extra === 0
  }

  #key(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, "").toLowerCase()
    } catch {
      return null
    }
  }
}
